import { Container, Sprite, Texture, Rectangle, Assets } from 'pixi.js';
import { TILE } from './world';

/**
 * A bought companion: the angel or the demon that follows you and strikes what
 * comes near.
 *
 * OWNER: "you can buy angel and demon, to be with you, which will kill the nearest
 * mobs, for 15000 shrooms."
 *
 * WHAT THIS IS NOT: a second combat system. The strike goes through the same
 * `mobs.hitAt` the player's sword uses, which already fires the death hook that
 * credits the kill and rolls the loot server-side — so a companion kill and a
 * player kill are the same event, priced by the same code.
 *
 * THREE LIMITS, and they are the whole reason an idle companion cannot be farmed
 * (the numbers live in backend companions.config.ts, which explains the balance):
 *   • LEASH — it never drifts more than a few tiles from the hero, so it cannot be
 *     parked in a spawn field while the player does something else.
 *   • ENGAGE measured from the HERO, not from the companion, so it adds no reach.
 *     It fights the fight the player is already in.
 *   • CADENCE — one strike per interval, no burst, no energy. Its damage per
 *     second is well under a swung sword.
 *
 * All three are enforced in `pickTarget` and `update` below, and pinned as pure
 * functions in companion.test.ts: jsdom has no layout engine and cannot drive
 * Pixi, so the geometry is what gets tested, not the rendering.
 */

/**
 * THE SHEET, established by reading it frame by frame rather than guessing — the
 * guess is what put the hero's mine and till animations on each other's rows for
 * weeks. Same convention the hero uses: three rows per state, down / side / up,
 * with `side` mirrored for left and right.
 *
 *   rows 0-2   6 frames   idle
 *   rows 3-5   6 frames   fly
 *   rows 6-8   8 frames   attack
 *   rows 9-11  4 frames   death (a white spirit form; unused)
 */
const FRAME = 64;
const SHEET_COLS = 8;
type Facing = 'down' | 'side' | 'up';
const FACE_IDX: Record<Facing, number> = { down: 0, side: 1, up: 2 };
const STATE_ROW = { idle: 0, fly: 3, attack: 6 } as const;
const STATE_FRAMES = { idle: 6, fly: 6, attack: 8 } as const;
type State = keyof typeof STATE_ROW;

/** Idle and fly loop gently; the strike plays faster because it is one blow. */
const FPS = { idle: 5, fly: 8, attack: 12 } as const;
/** How far off the hero's shoulder it hovers, in px. */
const OFFSET_X = TILE * 0.75;
const OFFSET_Y = -TILE * 0.55;
/** How fast it closes the gap to its perch. Faster than the hero so it keeps up. */
/**
 * IT MUST OUTRUN THE HERO, OR IT IS NOT A COMPANION.
 *
 * This was 6 tiles/sec against a hero who runs at 6.5 and rides at 8 — 10 on a
 * racing horse (GAIT in map/player.ts). Anything faster than a walk left it behind
 * permanently: the gap grew every frame, it drifted off screen, and the player was
 * fighting alone while a bought companion was somewhere back down the path. It is
 * pinned above the fastest mount, and it closes a large gap faster still (below).
 */
const FOLLOW_SPEED = TILE * 13;

/**
 * Beyond this it stops travelling and simply APPEARS at the hero.
 *
 * Speed alone cannot cover a discontinuity: respawn, unstick, entering the farm
 * from another view and any lag spike move the hero instantly, and gliding across
 * a farm thousands of pixels wide is the same disappearance by another route.
 */
const SNAP_TILES = 14;

/**
 * Where the companion is after one frame — pure, so the thing the player actually
 * complains about ("it disappears") is testable without a canvas.
 *
 * `hx`/`hy` are the HERO; `wantX`/`wantY` the leash-clamped perch. Kept separate
 * because the perch is always within leash of the hero and so can never reveal how
 * far behind the companion has really fallen.
 */
export function followStep(
  cx: number, cy: number,
  wantX: number, wantY: number,
  hx: number, hy: number,
  dt: number,
): { x: number; y: number; moving: boolean } {
  if (Math.hypot(hx - cx, hy - cy) > SNAP_TILES * TILE) return { x: wantX, y: wantY, moving: false };
  const dx = wantX - cx, dy = wantY - cy;
  const dist = Math.hypot(dx, dy);
  if (dist <= 2) return { x: cx, y: cy, moving: false };
  const urgency = Math.min(2.5, 1 + dist / (TILE * 8));
  const step = Math.min(dist, FOLLOW_SPEED * urgency * dt);
  return { x: cx + (dx / dist) * step, y: cy + (dy / dist) * step, moving: true };
}

export interface CompanionTarget {
  x: number;
  y: number;
  /** Set by mobs.ts; a dying mob must not be struck again. */
  state?: string;
}

export interface CompanionDef {
  kind: string;
  damage: number;
  attackEveryMs: number;
  engageTiles: number;
  leashTiles: number;
}

/**
 * The nearest strikeable hostile, or null.
 *
 * Range is measured from the HERO (hx, hy) deliberately — see the note above. A
 * companion that measured from itself would extend the player's reach by its own
 * leash every time it drifted, which is exactly the "park it and farm" case the
 * design forbids.
 */
export function pickTarget<T extends CompanionTarget>(
  mobs: readonly T[],
  hx: number,
  hy: number,
  engageTiles: number,
): T | null {
  const reach = engageTiles * TILE;
  let best: T | null = null;
  let bestD = Infinity;
  for (const m of mobs) {
    if (m.state === 'dying' || m.state === 'spawning') continue;
    const d = Math.hypot(m.x - hx, m.y - hy);
    if (d <= reach && d < bestD) { best = m; bestD = d; }
  }
  return best;
}

/**
 * Where the companion wants to be this frame: at its perch beside the hero, or
 * leaning toward a target — but never further from the hero than the leash.
 */
export function perchFor(
  hx: number, hy: number,
  target: { x: number; y: number } | null,
  leashTiles: number,
  facingLeft: boolean,
): { x: number; y: number } {
  const home = { x: hx + (facingLeft ? -OFFSET_X : OFFSET_X), y: hy + OFFSET_Y };
  if (!target) return home;
  // Lean a third of the way toward what it is hitting, so the strike reads as
  // aimed rather than fired from the shoulder — then clamp to the leash.
  const want = { x: home.x + (target.x - home.x) * 0.33, y: home.y + (target.y - home.y) * 0.33 };
  const d = Math.hypot(want.x - hx, want.y - hy);
  const max = leashTiles * TILE;
  if (d <= max) return want;
  const k = max / (d || 1);
  return { x: hx + (want.x - hx) * k, y: hy + (want.y - hy) * k };
}

export interface Companion {
  container: Container;
  /** Per frame. Returns the mob it struck this tick, if any, so the caller can spark it. */
  update(
    dtSec: number,
    heroX: number, heroY: number,
    mobs: readonly CompanionTarget[],
    now: number,
  ): { target: CompanionTarget; damage: number } | null;
  destroy(): void;
}

/**
 * Build the entity. Textures are loaded through Assets, never Texture.from(url):
 * an unloaded URL returns Texture.EMPTY in Pixi 8 and the sprite draws NOTHING,
 * which for a 15 000-Shroom purchase would look exactly like the money vanishing.
 */
export async function createCompanion(def: CompanionDef, atX = 0, atY = 0): Promise<Companion> {
  const url = `/sprites/companions/${def.kind}.png`;
  // Assets.load, never Texture.from(url): an unloaded URL returns Texture.EMPTY in
  // Pixi 8 and the sprite draws NOTHING, which for a 15 000-Shroom purchase looks
  // exactly like the money vanishing.
  const sheet = (await Assets.load(url)) as Texture;

  /** One row of the sheet as a frame list. */
  const row = (state: State, facing: Facing): Texture[] => {
    const r = STATE_ROW[state] + FACE_IDX[facing];
    const n = Math.min(STATE_FRAMES[state], SHEET_COLS);
    return Array.from({ length: n }, (_, i) => new Texture({
      source: sheet.source,
      frame: new Rectangle(i * FRAME, r * FRAME, FRAME, FRAME),
    }));
  };
  const clips: Record<State, Record<Facing, Texture[]>> = {
    idle: { down: row('idle', 'down'), side: row('idle', 'side'), up: row('idle', 'up') },
    fly: { down: row('fly', 'down'), side: row('fly', 'side'), up: row('fly', 'up') },
    attack: { down: row('attack', 'down'), side: row('attack', 'side'), up: row('attack', 'up') },
  };

  const container = new Container();
  container.eventMode = 'none';        // decoration: never swallows a tap meant for the world
  // START AT THE HERO. Without this the container sits at world origin (0,0) and
  // the follow glides it in at FOLLOW_SPEED — on a farm that is thousands of
  // pixels across, a bought companion spent ten seconds crawling from the corner
  // of the map and the player, quite reasonably, reported that it never appeared.
  container.x = atX;
  container.y = atY;
  const sprite = new Sprite(clips.idle.down[0]);
  sprite.anchor.set(0.5, 0.9);
  sprite.scale.set(TILE / 44);
  container.addChild(sprite);

  let state: State = 'idle';
  let facing: Facing = 'down';
  let flip = false;
  let animT = 0;
  let frameIdx = 0;
  let attackLeft = 0;                  // ms remaining of a one-shot strike
  let nextStrikeAt = 0;

  /** Which way to face, from a direction vector. Mirrors the hero's rule. */
  const faceOf = (dx: number, dy: number): { facing: Facing; flip: boolean } => {
    if (Math.abs(dx) >= Math.abs(dy)) return { facing: 'side', flip: dx < 0 };
    return { facing: dy < 0 ? 'up' : 'down', flip };
  };

  const setClip = (next: State) => {
    if (next === state) return;
    state = next; frameIdx = 0; animT = 0;
  };

  return {
    container,
    update(dt, hx, hy, mobs, now) {
      const target = pickTarget(mobs, hx, hy, def.engageTiles);
      const want = perchFor(hx, hy, target, def.leashTiles, flip);

      // Move toward the perch, and FACE the way we are actually travelling — that
      // is what "move in direction properly" means: the sheet has down/side/up
      // rows and until now only the front-facing one was ever drawn.
      const fromX = container.x, fromY = container.y;
      const moved = followStep(container.x, container.y, want.x, want.y, hx, hy, dt);
      container.x = moved.x;
      container.y = moved.y;
      const moving = moved.moving;
      // Face the way it ACTUALLY travelled this frame, not the way the perch lies:
      // after a snap those are different directions.
      const dx = container.x - fromX, dy = container.y - fromY;

      // A strike owns the facing for its duration: it must look at what it hits,
      // not at where it is drifting.
      if (attackLeft > 0) {
        attackLeft -= dt * 1000;
        if (attackLeft <= 0) setClip(moving ? 'fly' : 'idle');
      } else if (target) {
        const f = faceOf(target.x - container.x, target.y - container.y);
        facing = f.facing; if (f.facing === 'side') flip = f.flip;
        setClip(moving ? 'fly' : 'idle');
      } else if (moving) {
        const f = faceOf(dx, dy);
        facing = f.facing; if (f.facing === 'side') flip = f.flip;
        setClip('fly');
      } else {
        setClip('idle');
      }

      // Animate. The attack row plays ONCE and then hands back to fly/idle; the
      // loops cycle.
      const clip = clips[state][facing];
      animT += dt;
      if (animT >= 1 / FPS[state]) {
        animT = 0;
        frameIdx = state === 'attack' ? Math.min(frameIdx + 1, clip.length - 1) : (frameIdx + 1) % clip.length;
      }
      sprite.texture = clip[frameIdx] ?? clip[0];
      sprite.scale.x = Math.abs(sprite.scale.x) * (facing === 'side' && flip ? -1 : 1);
      container.zIndex = container.y;

      if (!target || now < nextStrikeAt) return null;
      nextStrikeAt = now + def.attackEveryMs;
      // Play the strike, and hold the facing at the target while it runs.
      const f = faceOf(target.x - container.x, target.y - container.y);
      facing = f.facing; if (f.facing === 'side') flip = f.flip;
      state = 'attack'; frameIdx = 0; animT = 0;
      attackLeft = (STATE_FRAMES.attack / FPS.attack) * 1000;
      return { target, damage: def.damage };
    },
    destroy() {
      container.destroy({ children: true });
    },
  };
}
